/**
 * Required tenant configuration fields for Clinical Trials facilities.
 */

import { TextInput } from "@risalabs_frontend_org/oasis-ui-kit";
import type { ClinicalTrialsFacilitySettings } from "../../../../data-model/healthFacility";

/** Props for the Clinical Trials tenant settings fieldset. */
interface ClinicalTrialsSettingsSectionProps {
  settings: ClinicalTrialsFacilitySettings;
  resetFormData: boolean;
  onChange: (settings: ClinicalTrialsFacilitySettings) => void;
}

/**
 * Renders and updates the nested settings required by backend TenantContext.
 *
 * @param props - Current tenant settings, reset signal, and change callback
 * @returns Clinical Trials settings fieldset
 */
const ClinicalTrialsSettingsSection = ({
  settings,
  resetFormData,
  onChange,
}: ClinicalTrialsSettingsSectionProps) => {
  /**
   * Returns a required-field error for blank values.
   *
   * @param value - Current text input value
   * @param label - Human-readable field description
   * @returns Empty string when valid, otherwise a required-field message
   */
  const requiredError = (value: string, label: string): string =>
    value.trim() ? "" : `Please provide ${label}`;

  return (
    <fieldset>
      <legend>Clinical Trials Tenant Settings</legend>
      <TextInput
        id="clinical_trials_site_id"
        label="Site ID"
        required
        resetField={resetFormData}
        defaultValue={settings.site_id}
        error={requiredError(settings.site_id, "a site ID")}
        onChange={(data: { value: string }) =>
          onChange({ ...settings, site_id: data.value })
        }
      />
      <TextInput
        id="clinical_trials_fhir_org_code"
        label="FHIR Organization Code"
        required
        resetField={resetFormData}
        defaultValue={settings.fhir.org_code}
        error={requiredError(
          settings.fhir.org_code,
          "a FHIR organization code",
        )}
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            fhir: { org_code: data.value },
          })
        }
      />
      <TextInput
        id="clinical_trials_ingest_credential_id"
        label="Ingest Credential ID"
        required
        resetField={resetFormData}
        defaultValue={settings.ingest.credential_id}
        error={requiredError(
          settings.ingest.credential_id,
          "an ingest credential ID",
        )}
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            ingest: { credential_id: data.value },
          })
        }
      />
      <TextInput
        id="clinical_trials_email_recipients"
        label="Email Recipients (comma separated)"
        required
        resetField={resetFormData}
        defaultValue={settings.email.recipients.join(", ")}
        error={
          settings.email.recipients.some((recipient) => recipient.trim())
            ? ""
            : "Please provide at least one email recipient"
        }
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            email: {
              ...settings.email,
              recipients: data.value.split(","),
            },
          })
        }
      />
      <TextInput
        id="clinical_trials_email_from_email"
        label="From Email"
        required
        resetField={resetFormData}
        defaultValue={settings.email.from_email}
        error={requiredError(settings.email.from_email, "a from email")}
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            email: { ...settings.email, from_email: data.value },
          })
        }
      />
      <TextInput
        id="clinical_trials_email_subject_prefix"
        label="Email Subject Prefix"
        required
        resetField={resetFormData}
        defaultValue={settings.email.subject_prefix}
        error={requiredError(
          settings.email.subject_prefix,
          "an email subject prefix",
        )}
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            email: { ...settings.email, subject_prefix: data.value },
          })
        }
      />
      <TextInput
        id="clinical_trials_dashboard_url"
        label="Dashboard URL"
        required
        resetField={resetFormData}
        defaultValue={settings.dashboard.url}
        error={requiredError(settings.dashboard.url, "a dashboard URL")}
        onChange={(data: { value: string }) =>
          onChange({
            ...settings,
            dashboard: { url: data.value },
          })
        }
      />
    </fieldset>
  );
};

export default ClinicalTrialsSettingsSection;
